import { usePaymentAnalytics } from '../../hooks/usePaymentAnalytics';
import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import type { BankPlatform } from '../../data/bankApps';
import { ELQR_BANKS, ElqrBank, getElqrLaunchUrl, getElqrQrValue } from '../../data/elqrBanks';
import { detectBankPlatform, isIosWebKit } from '../../lib/bankApps';
import { ElqrScreenshotQr } from './ElqrScreenshotQr';
import { usePaymentLanguage } from '../../lib/paymentLanguage';
import { ANALYTICS_EVENTS, AnalyticsProperties } from '../../lib/analytics';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

interface ElqrBankOptionsProps {
    compact?: boolean;
    payload: string;
    amount: string;
    currency: string;
    analyticsProperties: AnalyticsProperties;
    source?: string;
    platform?: BankPlatform;
}

export const ElqrBankOptions: React.FC<ElqrBankOptionsProps> = ({
    compact = false,
    payload,
    amount,
    currency,
    analyticsProperties,
    source,
    platform = detectBankPlatform(typeof navigator === 'undefined' ? undefined : navigator),
}) => {
    const trackAnalyticsEvent = usePaymentAnalytics();
    const { t } = usePaymentLanguage();
    const [selectedBank, setSelectedBank] = useState<ElqrBank | null>(null);
    const [qrOpen, setQrOpen] = useState(false);
    const qrRef = useRef<HTMLCanvasElement>(null);
    const mobile = platform === 'ios' || platform === 'android';
    const qrValue = getElqrQrValue(payload, source);
    const trackAction = (action: string, bank: ElqrBank | null, properties: AnalyticsProperties = {}) => {
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_ACTION_CLICKED, {
            ...analyticsProperties,
            requisite_type: 'elqr',
            platform,
            bank_id: bank?.id || 'other',
            action,
            ...properties,
        });
    };
    const trackBankClick = (bank: ElqrBank, interaction: 'open_app' | 'show_qr', buttonSource: string) => {
        trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_BANK_OPEN_CLICKED, {
            ...analyticsProperties,
            requisite_type: 'elqr',
            bank_id: bank.id,
            platform,
            interaction,
            button_source: buttonSource,
        });
    };
    const openQr = (bank: ElqrBank | null = null) => {
        if (bank) trackBankClick(bank, 'show_qr', 'bank_grid');
        trackAction('show_qr', bank, { button_source: bank ? 'bank_grid' : 'other_bank' });
        setSelectedBank(bank);
        setQrOpen(true);
    };
    const closeQr = (buttonSource: string) => {
        if (qrOpen) trackAction('close_qr', selectedBank, { button_source: buttonSource });
        setQrOpen(false);
    };
    const downloadQr = () => {
        trackAction('download_qr', selectedBank, { qr_source: 'generated', button_source: 'qr_dialog' });
        if (!qrRef.current) return;
        const link = document.createElement('a');
        link.href = qrRef.current.toDataURL('image/png');
        link.download = 'payment-qr.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
    };
    const qrOnlyBank = (bank: ElqrBank) =>
        bank.id === 'elqr' || bank.id === 'optima';

    const screenshotQr = platform === 'android'
        || (platform === 'ios' && isIosWebKit(typeof navigator === 'undefined' ? undefined : navigator));
    if (screenshotQr) return <ElqrScreenshotQr compact={compact} value={qrValue} amount={amount} currency={currency} />;

    return (
        <div className={compact ? 'elqr-bank-options-compact space-y-3' : 'space-y-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3'}>
            <p className="text-center text-base font-semibold text-gray-900 dark:text-slate-100">{t("Выберите банк для оплаты")}</p>
            <div className="grid grid-cols-2 gap-2">
                {ELQR_BANKS.map(bank => {
                    const className = compact
                        ? 'elqr-bank-tile flex min-h-[52px] items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-3 transition hover:border-blue-400 active:translate-y-px'
                        : 'flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 dark:border-slate-700 px-3 py-2 transition hover:border-gray-300 dark:hover:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-900 active:translate-y-px';
                    const logo = <img src={bank.logo} alt={bank.name} className="h-auto max-h-[26px] max-w-full object-contain" />;
                    return mobile && !qrOnlyBank(bank) ? (
                        // Direct navigation keeps the user gesture; do not delay or open a new window.
                        <a key={bank.id} href={getElqrLaunchUrl(bank, payload)} referrerPolicy="no-referrer"
                            onClick={() => trackBankClick(bank, 'open_app', 'bank_grid')}
                            aria-label={t("Открыть {bank}", { bank: bank.name })} className={className}>
                            {logo}
                        </a>
                    ) : (
                        <button key={bank.id} type="button" onClick={() => openQr(bank)}
                            aria-label={t("Открыть {bank}", { bank: bank.name })} className={className}>
                            {logo}
                        </button>
                    );
                })}
            </div>
            <p className="text-center text-xs text-gray-500 dark:text-slate-400">
                {t(mobile ? "Выберите банк для перехода в приложение или оплаты по QR-коду." : "Нажмите на банк, чтобы показать QR-код для оплаты.")}
            </p>
            <Button type="button" variant="outline" className="w-full" onClick={() => openQr()}>
                {t("Другой банк")}
            </Button>

            <Dialog open={qrOpen} onOpenChange={open => { if (!open) closeQr('dialog_dismiss'); }}>
                <DialogContent closeLabel={t("Закрыть")} className="max-h-[90dvh] max-w-sm overflow-y-auto rounded-2xl">
                    <DialogHeader className="items-center pr-5 text-center">
                        <DialogTitle>{t("QR-код для оплаты")}</DialogTitle>
                        <DialogDescription>{t("Отсканируйте QR-код в банковском приложении")}</DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 flex justify-center rounded-xl bg-white p-2">
                        <QRCodeCanvas ref={qrRef} value={qrValue} size={560} level="M" marginSize={4} style={{ width: '100%', maxWidth: 280, height: 'auto' }} />
                    </div>
                    <Button type="button" className="mt-4 w-full" onClick={downloadQr}>
                        {t("Скачать QR")}
                    </Button>
                    <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-gray-600 dark:text-slate-300">
                        <li>{t("Скачайте QR-код и сохраните его в галерею телефона.")}</li>
                        <li>{t("Откройте приложение банка и выберите оплату по QR-коду.")}</li>
                        <li>{t("Загрузите QR-код из галереи или отсканируйте его с другого устройства.")}</li>
                        <li>{t("Проверьте получателя и сумму, затем подтвердите оплату.")}</li>
                    </ol>
                    {selectedBank && !qrOnlyBank(selectedBank) && (
                        <Button asChild className="mt-4 w-full">
                            <a href={getElqrLaunchUrl(selectedBank, payload)} referrerPolicy="no-referrer"
                                onClick={() => trackBankClick(selectedBank, 'open_app', 'qr_dialog')}>{t("Открыть приложение банка")}</a>
                        </Button>
                    )}
                    <Button type="button" variant="outline" className="mt-2 w-full" onClick={() => closeQr('close_button')}>
                        {t("Закрыть")}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
};
